import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import ErpNutrientTableRow from 'components/desktop/Recipe/RecipeTabs/Components/RecipeErpViewNew/Detail/NutrientTableRow';
import { RecipeErpViewComponentProps } from 'components/desktop/Recipe/RecipeTabs/Components/RecipeErpViewNew/Index';
import * as State from 'components/desktop/Recipe/RecipeTabs/Components/RecipeErpViewNew/State';
import { Functor } from 'functions/Functor';
import * as Calculation from 'types/Recipe/Calculation';
import * as CalculatedNutrient from 'types/Recipe/Detail/ErpNew/CalculatedNutrient';
import * as CalculatedNutrientList from 'types/Recipe/Detail/ErpNew/CalculatedNutrientList';
import * as NutrientCategoryGroup from 'types/Recipe/Detail/ErpNew/NutrientCategoryGroup';
import * as Product from 'types/Recipe/Detail/ErpNew/Product';

type IProps = RecipeErpViewComponentProps & {
	ingredientId: string;
	segmentIndex: number;
	allProducts: Product.Type[];
	productLeft: Product.Type | null;
	productRight: Product.Type | null;
	setComparisonLeft: (productId: string | null) => void;
	setComparisonRight: (productId: string | null) => void;
	nutrients: CalculatedNutrientList.Type;
	calculation: Calculation.Type;
};

const Nutrients = (props: IProps) => {
	const { t } = useTranslation();

	const filterOutEmptyGroups = (groups: NutrientCategoryGroup.Type[]) => {
		return groups.filter((e) =>
			NutrientCategoryGroup.filterOutEmptyGroups(
				e,
				props.productLeft?.nutrients ?? {},
				props.productRight?.nutrients ?? {}
			)
		);
	};

	const nutrientCategoryGroups: NutrientCategoryGroup.Type[] = Functor.From(props.nutrients)
		.map(NutrientCategoryGroup.getFromNutrientList)
		.map(filterOutEmptyGroups)
		.get();

	const renderSelect = (direction: 'left' | 'right'): JSX.Element => {
		const value =
			direction === 'left'
				? props.productLeft?.productId ?? 'default'
				: props.productRight?.productId ?? 'default';
		const comparisonFunction =
			direction === 'left' ? props.setComparisonLeft : props.setComparisonRight;

		return (
			<select
				className="form-select form-select-sm"
				onChange={(e) => comparisonFunction(e.target.value)}
				value={value}
			>
				<option value="default">AIR</option>;
				{props.allProducts.map((e, i) => {
					return (
						<option key={i} value={e.productId}>
							{`${e.name} (${e.sourceId})`}
						</option>
					);
				})}
			</select>
		);
	};

	const nutrientsLeft: { [nutrientId: string]: number | null } = State.getNutrients(
		{
			ingredientId: props.ingredientId,
			calculation: props.calculation,
		},
		props.productLeft === null ? undefined : props.productLeft
	);

	const nutrientsRight: { [nutrientId: string]: number | null } = State.getNutrients(
		{
			ingredientId: props.ingredientId,
			calculation: props.calculation,
		},
		props.productRight === null ? undefined : props.productRight
	);

	return (
		<fieldset className="margin-top-25">
			<div className="d-flex justify-content-between" style={{ gap: '10px' }}>
				<div className="d-flex flex-gap-10">
					<legend style={{ width: 'auto' }}>{t('_general:NUTRIENTS')}</legend>
					<FontAwesomeIcon
						style={{ marginTop: 4 }}
						title={t('recipe:INFO_CALCULATION_ON_ENTRYLANGUAGE')}
						icon={faCircleInfo}
					></FontAwesomeIcon>
				</div>

				<div
					className="d-flex"
					style={{
						gap: '10px',
						marginBottom: nutrientCategoryGroups.length ? '10px' : undefined,
						maxWidth: '450px',
					}}
				>
					{renderSelect('left')}
					{renderSelect('right')}
				</div>
			</div>
			{nutrientCategoryGroups.map((group: NutrientCategoryGroup.Type, i: number) => (
				<div key={i}>
					<div className="table-header">{group.name}</div>
					<div className="scrollable-table-wrapper" style={{ height: 'auto' }}>
						<table className="align-middle table table-striped table-hover table-sm">
							<thead className="recipe-nutrients-thead">
								<tr>
									<th>
										<label>{t('recipe:NUTRIENT')}</label>
									</th>
									<th className="text-end">
										<label>{t('_general:SOURCE')} (L)</label>
									</th>
									<th className="text-end">
										<label>{t('_general:VALUE')} (L)</label>
									</th>
									<th className="text-end">
										<label>{t('_general:SOURCE')} (R)</label>
									</th>
									<th className="text-end">
										<label>{t('_general:VALUE')} (R)</label>
									</th>
								</tr>
							</thead>
							<tbody className="recipe-nutrients-tbody">
								{group.nutrients.map(
									(nutrient: CalculatedNutrient.Type, i: number) => {
										return (
											<ErpNutrientTableRow
												nutrient={nutrient}
												key={i}
												sourceLeft={props.productLeft?.sourceId ?? 'AIR'}
												sourceRight={props.productRight?.sourceId ?? 'AIR'}
												valueLeft={nutrientsLeft[nutrient.id ?? ''] ?? null}
												valueRight={
													nutrientsRight[nutrient.id ?? ''] ?? null
												}
											/>
										);
									}
								)}
							</tbody>
						</table>
					</div>
				</div>
			))}
		</fieldset>
	);
};

export default Nutrients;
